/**
 * Модальное окно с деталями заказа
 *
 * Показывает:
 * - Список выбранных блюд с ценами
 * - Управление количеством каждой позиции
 * - Итоговую сумму с учётом скидок
 * - Кнопку создания заказа
 */
import React from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { BottomSheet } from '../../../components/UI/BottomSheet';
import { OrderSummaryModalProps } from '../types';
import { formatPrice } from '../../../config/currency';

export const OrderSummaryModal: React.FC<OrderSummaryModalProps> = ({
  isOpen,
  onClose,
  selectedDishes,
  menuData,
  onRemoveDish,
  onUpdateQuantity,
  onCreateOrder,
  calculateFinalPrice,
  calculateTotal,
}) => {
  const { t } = useLanguage();

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={t('addOrder.orderDetails')}>
      <div className="space-y-4">
        {/* Список выбранных блюд */}
        <div className="space-y-3">
          {selectedDishes.map((item) => {
            const finalPrice = calculateFinalPrice(item);
            // Проверяем наличие ручной скидки
            const hasItemDiscount =
              item.allowItemDiscount &&
              item.itemDiscountPercent &&
              item.itemDiscountPercent > 0;
            // Проверяем наличие скидки по промокоду
            const hasPromoDiscount =
              item.allowPromo &&
              menuData?.promo.is_valid &&
              menuData.promo.discount_percent;
            // Есть ли какая-либо скидка
            const hasAnyDiscount = hasItemDiscount || hasPromoDiscount;

            return (
              <div
                key={`${item.dishId}-${item.portionId}`}
                className="p-4 bg-background-100 rounded-lg"
              >
                {/* Название и кнопка удаления */}
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <p className="font-semibold text-text-primary text-base">
                      {item.dishName}
                    </p>
                    {/* Информация о порции и цене */}
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="text-text-secondary">
                        {item.weight}
                        {t('addOrder.grams')} ×
                      </span>
                      {hasAnyDiscount ? (
                        <>
                          {/* Зачёркнутая оригинальная цена */}
                          <span className="text-text-secondary line-through">
                            {formatPrice(item.price)}
                          </span>
                          {/* Цена со скидкой */}
                          <span className="text-green-600 font-semibold">
                            {formatPrice(finalPrice)}
                          </span>
                        </>
                      ) : (
                        <span className="text-text-secondary">
                          {formatPrice(item.price)}
                        </span>
                      )}
                    </div>
                    {/* Показываем информацию о ручной скидке */}
                    {hasItemDiscount && (
                      <span className="inline-block mt-1 text-xs text-purple-600 font-medium">
                        -{item.itemDiscountPercent}% {t('addOrder.manualDiscount')}
                      </span>
                    )}
                  </div>

                  {/* Кнопка удаления */}
                  <button
                    onClick={() => onRemoveDish(item.dishId, item.portionId)}
                    className="text-red-500 hover:text-red-700 ml-2 p-1"
                    title={t('addOrder.remove')}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {/* Управление количеством */}
                <div className="flex items-center gap-3">
                  {/* Кнопка уменьшения */}
                  <button
                    onClick={() =>
                      onUpdateQuantity(item.dishId, item.portionId, item.quantity - 1)
                    }
                    className="w-9 h-9 flex items-center justify-center bg-background-200 hover:bg-background-300 rounded-lg transition-colors text-lg font-bold"
                  >
                    −
                  </button>

                  {/* Поле ввода количества */}
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) =>
                      onUpdateQuantity(
                        item.dishId,
                        item.portionId,
                        parseInt(e.target.value) || 1
                      )
                    }
                    className="w-16 text-center px-3 py-2 border border-background-300 rounded-lg text-base font-semibold"
                  />

                  {/* Кнопка увеличения */}
                  <button
                    onClick={() =>
                      onUpdateQuantity(item.dishId, item.portionId, item.quantity + 1)
                    }
                    className="w-9 h-9 flex items-center justify-center bg-background-200 hover:bg-background-300 rounded-lg transition-colors text-lg font-bold"
                  >
                    +
                  </button>

                  {/* Итоговая цена за позицию */}
                  <span className="text-text-primary font-semibold text-base ml-auto">
                    {formatPrice(finalPrice * item.quantity)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Итого и кнопка создания */}
        <div className="border-t-2 border-background-300 pt-4">
          {/* Итоговая сумма */}
          <div className="flex justify-between items-center mb-3">
            <span className="text-text-primary font-bold text-xl">
              {t('addOrder.total')}:
            </span>
            <span className="text-primary-500 font-bold text-2xl">
              {formatPrice(calculateTotal())}
            </span>
          </div>

          {/* Информация о скидке */}
          {menuData?.promo.is_valid && menuData.promo.discount_percent && (
            <p className="text-green-600 text-sm text-right mb-3">
              {t('addOrder.discount')}: {menuData.promo.discount_percent}%
            </p>
          )}

          {/* Кнопка создания заказа */}
          <button onClick={onCreateOrder} className="btn btn-primary w-full text-lg py-3">
            {t('addOrder.createOrder')}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
};
